import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { FadeUp } from "@/components/site/Reveal";
import api from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

const CATEGORIES = ["All", "Industry", "Operations", "Sustainability", "News"];

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = active === "All" ? "/blog" : `/blog?category=${encodeURIComponent(active)}`;
    api.get(url).then(({ data }) => setPosts(data)).catch(() => setPosts([])).finally(() => setLoading(false));
  }, [active]);

  return (
    <div data-testid="blog-list-page" className="bg-background">
      <Navbar />
      <header className="pt-40 pb-16 md:pt-48 md:pb-20 border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <FadeUp>
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary mb-6">/ Journal</p>
            <h1 className="font-display text-6xl md:text-8xl leading-[0.9]">News & Insights</h1>
            <p className="text-muted-foreground max-w-xl mt-8">
              Perspectives on the paper and paperboard supply chain — market analysis, operations and sustainability.
            </p>
          </FadeUp>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-14">
        <div className="flex flex-wrap gap-3 mb-14">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`blog-filter-${c.toLowerCase()}`}
              className={`px-5 py-2 font-mono2 text-xs uppercase tracking-[0.15em] border transition-colors ${
                active === c ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="font-mono2 text-sm text-muted-foreground py-20" data-testid="blog-loading">Loading…</p>
        ) : posts.length === 0 ? (
          <p className="font-mono2 text-sm text-muted-foreground py-20" data-testid="blog-empty">No articles in this category yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((p, i) => (
              <FadeUp key={p.id} delay={(i % 3) * 0.08}>
                <Link to={`/blog/${p.slug}`} className="group block bg-card border border-border h-full" data-testid={`blog-card-${i}`}>
                  <div className="overflow-hidden aspect-[16/10]">
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 font-mono2 text-[11px] uppercase tracking-[0.15em] text-primary mb-4">
                      <span>{p.category}</span>
                      <span className="text-muted-foreground">{fmtDate(p.created_at)}</span>
                    </div>
                    <h2 className="font-display text-2xl leading-tight mb-3">{p.title}</h2>
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-5">{p.excerpt}</p>
                    <span className="font-mono2 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-1.5 group-hover:text-primary transition-colors">
                      Read article <ArrowUpRight size={13} />
                    </span>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
